import { Job, Location } from '@mp/graphql/generated/graphql';
import { pluralize } from '@mp/utils';
import { format } from 'date-fns-tz';
import { tz } from 'moment-timezone';

const formatPhoneNumber = (str: string): string | null => {
  const cleaned = ('' + str).replace(/\D/g, '');
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return ['(', match[2], ') ', match[3], '-', match[4]].join('');
  }

  return null;
};

const setDateToLocalTimezone = (date: string) => {
  const dateObj = new Date(date);
  const dateOnly = new Date(dateObj.valueOf() + dateObj.getTimezoneOffset() * 60 * 1000);

  return dateOnly;
};

const fullDayDate = (job: Pick<Job, 'starts_date' | 'current_starts_time'>) => {
  const date = job.starts_date || job.current_starts_time;
  if (!date) {
    return null;
  }

  return format(setDateToLocalTimezone(date), 'EEEE MM/dd/yy');
};
interface PositionDisplayInfo {
  display_name?: string | null;
}

interface JobWithPositions {
  positions?: PositionDisplayInfo[] | null;
}

const positions = (job: JobWithPositions): string => {
  return job.positions?.map((position) => position.display_name).join(', ') || '';
};

type SpecialtyItem = {
  label?: string | null;
};

const formatSpecialties = (
  job: { specialties?: SpecialtyItem[] | null },
  isMobile: boolean,
  maxLabelSize?: number,
): string => {
  const specialtiesCount = job.specialties?.length ?? 0;
  const hasMultipleSpecialties = specialtiesCount > 1;
  const label = job.specialties?.[0].label ?? '';
  const isBigLabel = (label.length ?? 0) > (maxLabelSize ?? Infinity);
  const truncateLabel = isMobile && isBigLabel;

  const specialty = truncateLabel ? `${label.slice(0, maxLabelSize)}...` : label;
  const otherSpecialties = hasMultipleSpecialties ? ` +${specialtiesCount - 1} more` : '';

  return specialty.concat(otherSpecialties);
};

type JobTimeLocation = Pick<Location, 'timezone_lookup'>;

type JobTimeJob = Pick<
  Job,
  | 'current_starts_time'
  | 'approved_clock_in'
  | 'current_ends_time'
  | 'approved_clock_out'
  | 'guaranteed_minimum_hours'
  | 'status'
> & {
  location?: JobTimeLocation | undefined | null;
};

function time(
  job: JobTimeJob,
  {
    withShiftLength = true,
    scheduledShiftStyle = false,
  }: { withShiftLength?: boolean; scheduledShiftStyle?: boolean } = {},
): string | null {
  const format = 'HH:mm';
  const {
    current_starts_time,
    approved_clock_in,
    current_ends_time,
    approved_clock_out,
    location,
    guaranteed_minimum_hours,
    status,
  } = job;
  if (!current_starts_time || !current_ends_time) {
    return null;
  }

  const timezone = location?.timezone_lookup || tz.guess();
  const startsTime = approved_clock_in || current_starts_time;
  const endsTime = approved_clock_out || current_ends_time;
  const start = tz(startsTime, timezone);
  const end = tz(endsTime, timezone);
  const hoursDiff = end.diff(start, 'hours', true).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  const hours =
    status === 'canceled_with_fee'
      ? guaranteed_minimum_hours
      : Math.max(guaranteed_minimum_hours ?? 0, +hoursDiff);

  return scheduledShiftStyle
    ? `${start.format(format)} - ${end.format(format)}${
        withShiftLength && hours ? ` (${pluralize(hours, 'hr')})` : ``
      }`
    : `${start.format(format)}-${end.format(format)}${
        withShiftLength && hours ? ` (${pluralize(hours, 'hr')})` : ``
      }`;
}

const JobDisplayHelpers = {
  formatPhoneNumber,
  formatSpecialties,
  fullDayDate,
  positions,
  time,
};

export default JobDisplayHelpers;
